import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FilterBar,
  PageHeader,
  SearchField,
  Select,
  Skeleton,
  Table,
} from "@evolua/ui";
import { EvoluaApiError } from "@evolua/api-client";
import { useMemo, useState } from "react";
import { useDownloadMeDocument, useMeDocuments } from "../api/use-me.js";

/** Documentos do colaborador com download auditado (FASE-06-T38). */
export function DocumentosPage() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const filters = useMemo(
    () => ({
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(category ? { category } : {}),
    }),
    [q, category],
  );
  const list = useMeDocuments(filters);
  const download = useDownloadMeDocument();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastAudit, setLastAudit] = useState<string | null>(null);

  const correlationId =
    list.error instanceof EvoluaApiError ? list.error.problem.traceId : undefined;

  async function onDownload(id: string) {
    setActionError(null);
    setBusyId(id);
    try {
      const result = await download.mutateAsync(id);
      const blob = new Blob([result.content], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      setLastAudit(result.auditId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha no download");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Documentos"
        lead="Documentos do seu vínculo com busca e categorias. Downloads usam URL assinada de curta duração e são auditados."
      />

      <FilterBar>
        <SearchField
          label="Buscar"
          placeholder="Buscar…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar documentos"
        />
        <Select
          label="Categoria"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Categoria"
          options={[
            { value: "", label: "Todas as categorias" },
            ...(list.data?.categories ?? []).map((c) => ({ value: c, label: c })),
          ]}
        />
      </FilterBar>

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}
      {lastAudit ? (
        <p className="evolua-muted">Último download auditado: {lastAudit}</p>
      ) : null}

      {list.isPending ? (
        <Skeleton height="8rem" label="Documentos" />
      ) : list.isError || !list.data ? (
        <ErrorState
          {...(correlationId ? { correlationId } : {})}
          onRetry={() => void list.refetch()}
        />
      ) : !list.data.capabilityAvailable ? (
        <EmptyState
          title="Módulo indisponível"
          description="O ERP deste cliente não disponibiliza documentos digitais. Quando o módulo for contratado, eles aparecem aqui."
        />
      ) : list.data.data.length === 0 ? (
        <EmptyState
          title="Nenhum documento"
          description="Não há documentos para os filtros atuais. Sem documentos no ERP, esta lista fica vazia de forma explícita."
        />
      ) : (
        <Table
          caption="Documentos"
          getRowId={(doc) => doc.id}
          columns={[
            { id: "name", header: "Documento", cell: (doc) => doc.name },
            {
              id: "category",
              header: "Categoria",
              cell: (doc) => <Badge tone="neutral">{doc.category}</Badge>,
            },
            {
              id: "dates",
              header: "Validade",
              cell: (doc) => (
                <span className="evolua-muted">
                  {doc.issuedAt ? `Emitido em ${doc.issuedAt}` : "Sem data de emissão"}
                  {doc.expiresAt ? ` · válido até ${doc.expiresAt}` : ""}
                </span>
              ),
            },
            {
              id: "status",
              header: "Status",
              cell: (doc) =>
                doc.expired ||
                (doc.expiresAt &&
                  doc.expiresAt < new Date().toISOString().slice(0, 10)) ? (
                  <Badge tone="warning">Vencido</Badge>
                ) : (
                  "—"
                ),
            },
            {
              id: "actions",
              header: "Ações",
              cell: (doc) => (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={busyId === doc.id || download.isPending}
                  onClick={() => void onDownload(doc.id)}
                >
                  {busyId === doc.id ? "Baixando…" : "Baixar"}
                </Button>
              ),
            },
          ]}
          rows={list.data.data}
        />
      )}
    </>
  );
}
